import React, { useEffect, useState } from "react";
import { Share2 } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "../lib/api";
import { useLang } from "../context/LanguageContext";

const FALLBACK = "https://images.unsplash.com/photo-1610970878459-a0e464d7592b?auto=format&fit=crop&w=1000&q=80";

export const Products = () => {
  const { t, pick } = useLang();
  const [products, setProducts] = useState([]);
  const [activeCat, setActiveCat] = useState("all");

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);

  const categories = Array.from(new Set(products.map((p) => (p.category || "other").toLowerCase())));
  const shown = activeCat === "all" ? products : products.filter((p) => (p.category || "other").toLowerCase() === activeCat);

  const share = async (p) => {
    const url = window.location.origin + "/products";
    const text = `${pick(p, "name")} @ Panda Tata 🍔`;
    if (navigator.share) {
      try { await navigator.share({ title: "Panda Tata", text, url }); } catch (e) { /* share cancelled */ }
    } else {
      await navigator.clipboard.writeText(`${text} ${url}`);
      toast.success("Link copied!");
    }
  };

  return (
    <div>
      <section className="bg-brand-yellow grain">
        <div className="mx-auto max-w-7xl px-5 py-16">
          <h1 className="font-display text-5xl font-black tracking-tighter sm:text-7xl">{t("products_title")}</h1>
          <p className="mt-4 max-w-xl text-lg text-brand-ink/80">{t("products_sub")}</p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-14">
        {products.length === 0 && <p className="text-muted-foreground">Menu coming soon…</p>}
        {categories.length > 1 && (
          <div className="mb-8 flex flex-wrap gap-2" data-testid="category-filter">
            <button onClick={() => setActiveCat("all")} data-testid="cat-all"
              className={`rounded-full border-2 border-brand-ink px-4 py-1.5 text-sm font-bold capitalize transition-transform hover:-translate-y-0.5 ${activeCat === "all" ? "bg-brand-ink text-brand-yellow" : "bg-white"}`}>
              {t("all_categories")}
            </button>
            {categories.map((c) => (
              <button key={c} onClick={() => setActiveCat(c)} data-testid={`cat-${c}`}
                className={`rounded-full border-2 border-brand-ink px-4 py-1.5 text-sm font-bold capitalize transition-transform hover:-translate-y-0.5 ${activeCat === c ? "bg-brand-green" : "bg-white"}`}>
                {c}
              </button>
            ))}
          </div>
        )}
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((p) => (
            <div key={p.id} data-testid={`product-card-${p.id}`} className="group flex flex-col overflow-hidden rounded-3xl border-2 border-brand-ink bg-white hover-brutal">
              <div className="relative overflow-hidden">
                <img src={p.image_path ? fileUrl(p.image_path) : FALLBACK} alt={pick(p, "name")} className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <span className="absolute left-3 top-3 rounded-full border-2 border-brand-ink bg-brand-green px-3 py-1 text-xs font-bold uppercase">{p.category || "signature"}</span>
              </div>
              <div className="flex flex-1 flex-col p-5">
                <h3 className="font-display text-2xl font-bold">{pick(p, "name")}</h3>
                <p className="mt-2 flex-1 text-sm text-muted-foreground">{pick(p, "description")}</p>
                <div className="mt-5">
                  <button onClick={() => share(p)} data-testid={`product-share-${p.id}`} className="inline-flex w-full items-center justify-center gap-2 rounded-full border-2 border-brand-ink bg-brand-yellow px-4 py-2.5 text-sm font-bold hover-brutal">
                    <Share2 className="h-4 w-4" /> {t("share")}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Products;
